import {
  CalendarDays,
  Clapperboard,
  createLucideIcon,
  Gamepad2,
  HandFist,
  Music2,
  Radio,
  Trophy,
  UserRound,
  type IconNode,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// Lucide lab "soccer-ball" (not in the core set) inlined as an icon node.
const SoccerBall = createLucideIcon('SoccerBall', [
  ['circle', { cx: '12', cy: '12', r: '10', key: '1mglay' }],
  [
    'path',
    {
      d: 'M11.9 6.7s-3 1.3-5 3.6c0 0 0 3.6 1.9 5.9 0 0 3.1.7 6.2 0 0 0 1.9-2.3 1.9-5.9 0 .1-2-2.3-5-3.6',
      key: '1mg18b',
    },
  ],
  ['path', { d: 'M11.9 6.7V2', key: '1t12cm' }],
  ['path', { d: 'M16.9 10.4s3-1.4 4.5-1.6', key: '8aq2q9' }],
  ['path', { d: 'M15 16.3s1.9 2.7 2.9 3.7', key: 'volqrc' }],
  ['path', { d: 'M8.8 16.3S6.9 19 6 20', key: '1atvv1' }],
  ['path', { d: 'M2.6 8.7C4 9 7 10.4 7 10.4', key: '1uixp3' }],
] as IconNode);

const POPULARITY_MAX = 5;

const EVENT_TYPE_ICONS: Record<string, LucideIcon> = {
  soccer: SoccerBall,
  "formula 1": Trophy,
  "fighting sport": HandFist,
  concert: Music2,
  music: Music2,
  movie: Clapperboard,
  film: Clapperboard,
  gaming: Gamepad2,
  esports: Gamepad2,
  broadcast: Radio,
};

export function EventTypeIcon({
  eventType,
  className = 'h-4 w-4',
}: {
  eventType: string;
  className?: string;
}) {
  const Icon = EVENT_TYPE_ICONS[eventType.toLowerCase()] ?? CalendarDays;
  return <Icon className={className} aria-hidden />;
}

export function CampaignLiveDot() {
  return (
    <span className="relative flex h-2 w-2 shrink-0" aria-label="Live event">
      <span className="absolute inline-flex h-full w-full animate-ping motion-reduce:animate-none rounded-full bg-status-error opacity-60" />
      <span className="relative inline-flex h-2 w-2 rounded-full bg-status-error" />
    </span>
  );
}

export function PopularityMeter({ value }: { value: number | null }) {
  if (value == null) {
    return <span className="text-2xs text-muted">—</span>;
  }

  const score = Math.ceil(Math.max(0, Math.min(10, value)) / 2);

  return (
    <span
      className="inline-flex items-center gap-0.5 text-brand-cyan"
      title={`Popularity ${score}/${POPULARITY_MAX} (audience)`}
      role="img"
      aria-label={`Popularity ${score} of ${POPULARITY_MAX}`}
    >
      {Array.from({ length: score }, (_, index) => (
        <UserRound key={index} className="size-3.5 fill-current" aria-hidden />
      ))}
    </span>
  );
}
